import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import RolesSelector from 'components/Shared/Selectors/RolesSelector';
import LinkOrSpan from 'components/Shared/LinkOrSpan';
import { toPlural, toSeoUrl } from 'helpers/HelperFunctions';
import userShape from 'propTypes/userShape';
import styles from './Roles.module.scss';
import displayNameStyles from './DisplaynameSection.module.scss';
import { useDispatch } from 'react-redux';
import { update as updateUser } from 'redux/modules/auth';
import useAuth from 'hooks/useAuth';
import { trackEvent } from 'helpers/Analytics';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import hocStyles from '../../PortfolioAbout/components/hoc/hoc.module.scss';
import Loader from 'components/Loader/Loader';

const { item } = displayNameStyles;

const { editBtn, editWrp, loader, inHeader } = hocStyles;

const Roles = ({ roles, user, isOwner }) => {
  const { lastUpdated, updating, updated } = useAuth();

  const dispatch = useDispatch();
  const [editRoles, setEditRoles] = useState(roles || []);
  const [editing, setEditing] = useState(false);

  // Update editRoles when roles prop changes (e.g., after resume upload)
  useEffect(() => {
    setEditRoles(roles || []);
  }, [roles]);

  const handleEdit = () => {
    if (JSON.stringify(editRoles) !== JSON.stringify(roles)) {
      dispatch(
        updateUser(
          user,
          { featured_roles: editRoles.length > 0 ? editRoles : [] },
          false,
          [],
          '',
          'featured_roles'
        )
      ).then(() => {
        trackEvent('freelancer-updates-roles');
        setEditing(false);
      });
    } else {
      setEditing(false);
    }
  };

  const instanceIsUpdating = useMemo(
    () => lastUpdated === 'featured_roles',
    [lastUpdated]
  );

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !editing && (
      <SvgIcon
        style={{ marginTop: '-.5em', marginLeft: '5px' }}
        size="25"
        icon="tick_circle"
        color="#0fe2af"
      />
    );

  const hasRoles = roles && roles.length > 0;

  // Avoid leaving the min-height roles row empty for visitors
  if (!hasRoles && !isOwner) {
    return null;
  }

  return (
    <div
      id="portfolio-roles"
      className={styles.selectorContainer}
      style={{ marginRight: '5px' }}
    >
      <div>
        {hasRoles ? (
          <div className={inHeader}>
            <div style={{ marginTop: '-8px' }}>
              {roles.map((role, key, arr) => (
                <span key={`PortfolioHeader-roles-${key}`}>
                  {key < 3 && (
                    <LinkOrSpan
                      active={!isOwner}
                      spanProps={{
                        className: item,
                        key,
                      }}
                      linkProps={{
                        className: item,
                        key,
                        to: `/find/${toSeoUrl(toPlural(role))}`,
                      }}
                    >
                      {role}
                      {arr.length - 1 === key ? null : ', '}
                    </LinkOrSpan>
                  )}
                </span>
              ))}

              {roles.length > 3 && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Popover id="other-user-roles" placement="bottom">
                      <span className="lato-bold">Other roles: </span>
                      {roles.slice(3).map((role, index, array) => (
                        <Fragment key={`other-role-${index}`}>
                          {role}
                          {index < array.length - 1 ? ', ' : ''}
                        </Fragment>
                      ))}
                    </Popover>
                  }
                >
                  <span className={item}> +{roles.length - 3}</span>
                </OverlayTrigger>
              )}
            </div>
            {user && isOwner && (
              <div className={editWrp}>
                {successTick}
                {instanceIsUpdating && updating ? (
                  <Loader loading className={loader} />
                ) : (
                  <button
                    id="edit-bio"
                    className={editBtn}
                    onClick={() => setEditing(true)}
                    type="button"
                  >
                    <SvgIcon size="16" icon="pencil" color="#ffffff" />
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.addRoles} onClick={() => setEditing(true)}>
            Add your roles
          </div>
        )}
        <Modal show={editing && isOwner} onHide={handleEdit}>
          <ModalHeader closeButton>
            <ModalTitle className="lato-bold" style={{ marginTop: '2.5px' }}>
              Select your roles
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div>
              Select up to 10 roles from the dropdown below. You can also type
              to search for your matching roles.
            </div>
            <RolesSelector
              existing={editRoles || []}
              onSelect={(selected) =>
                setEditRoles(selected.map(({ value }) => value))
              }
              maxValues={10}
              styles={{
                control: (base) => ({ ...base, minWidth: '150px' }),
              }}
              className="mt+"
            />
          </ModalBody>
          <ModalFooter>
            <Button
              disabled={updating}
              onClick={() => {
                setEditing(false);
                setEditRoles(roles);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={updating}
              onClick={handleEdit}
              bsStyle="primary"
              className="ml--"
            >
              Save
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

Roles.propTypes = {
  roles: PropTypes.array,
  editMode: PropTypes.bool,
  updateData: PropTypes.func,
  isOwner: PropTypes.bool,
  toggleEditMode: PropTypes.func,
  user: userShape,
};

export default Roles;
